import { Component } from "@theme/component";

/**
 * Navigation component - Mobile and Desktop Navigation Handler
 *
 * Manages comprehensive navigation interactions including:
 * - Mobile hamburger menu toggle
 * - Sub-navigation drawers and overlays
 * - Accordion menus
 * - Desktop drawer navigation
 * - Promo bar carousel (Swiper)
 * - Feature callout carousels (Swiper)
 * - Gorgias chat integration
 * - Back navigation
 * - Overlay click-to-close
 *
 * @extends {Component}
 */
/* global GorgiasChat */
class NavigationComponent extends Component {
  constructor() {
    super();
    this.dom = {};
    this.swiperInstances = [];
    this.eventHandlers = [];
    this.scrollbarWidth = 0;
    /** @type {HTMLElement|null} Stores the trigger that opened the current drawer for focus return */
    this._lastActiveTrigger = null;
    /** @type {((e: KeyboardEvent) => void)|null} */
    this._focusTrapHandler = null;
    /** @type {HTMLElement|null} */
    this._focusTrapContainer = null;
  }

  /**
   * Calculate the width of the browser scrollbar
   * @returns {number} The scrollbar width in pixels
   * @private
   */
  #getScrollbarWidth() {
    // Cache the scrollbar width calculation
    if (this.scrollbarWidth === 0) {
      this.scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    }
    return this.scrollbarWidth;
  }

  /**
   * Lock body scroll and compensate for scrollbar width to prevent layout shift
   * @private
   */
  #lockBodyScroll() {
    const scrollbarWidth = this.#getScrollbarWidth();
    document.body.style.overflow = "hidden";
    document.body.style.paddingRight = `${scrollbarWidth}px`;

    // Apply padding to fixed/sticky elements to prevent layout shift
    const stickyHeader = document.querySelector("sticky-header-wrapper");
    if (stickyHeader instanceof HTMLElement) {
      stickyHeader.style.paddingRight = `${scrollbarWidth}px`;
    }
  }

  /**
   * Unlock body scroll and remove scrollbar compensation
   * @private
   */
  #unlockBodyScroll() {
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";

    // Remove padding from fixed/sticky elements
    const stickyHeader = document.querySelector("sticky-header-wrapper");
    if (stickyHeader instanceof HTMLElement) {
      stickyHeader.style.paddingRight = "";
    }
  }

  /**
   * Called when component is added to the DOM
   */
  connectedCallback() {
    super.connectedCallback();
    this.#cacheDom();
    this.#bindUIActions();
  }

  /**
   * Called when component is removed from the DOM
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  /**
   * Cache all DOM elements for navigation
   * @private
   */
  #cacheDom() {
    this.dom.navToggle = this.querySelector(".js-nav__toggle");
    this.dom.navDrawer = this.querySelector(".js-header__navigation");
    this.dom.navTriggers = this.querySelectorAll(".js-subNavTrigger");
    this.dom.navBack = this.querySelectorAll(".js-collapsible-back");
    this.dom.navAccordions = this.querySelectorAll(".js-menuAccordion");
    this.dom.desktopNavTriggers = this.querySelectorAll(".sidebarMenu__mainTrigger");
    this.dom.overlay = this.querySelector(".sidebarMenu__drawerOverlay");
    this.dom.drawerTriggers = this.querySelectorAll(".sidebarMenu__drawerTrigger");
    this.dom.closeNavBtn = this.querySelector(".header__closeNav");
    this.dom.mobileNavClose = this.querySelector(".js-mobile-nav-close");
    this.dom.triggerGorgiasChat = this.querySelector(".js-sidebarMenu__contactTopItem--chat");
    this.dom.promoBarSlider = document.querySelector(".js-promoBar__carousel");
    this.dom.promoSlides = document.querySelectorAll(".announcement__bar");
    this.dom.header = this.querySelector("header-component");
  }

  /**
   * Bind all UI event handlers
   * @private
   */
  #bindUIActions() {
    // Initialize inert attribute if drawer starts hidden
    // Remove inert attribute above 1280px on load
    if (this.dom.navDrawer?.getAttribute("aria-hidden") === "true") {
      if (window.innerWidth >= 1280) {
        this.dom.navDrawer.removeAttribute("inert");
      } else {
        this.dom.navDrawer.setAttribute("inert", "");
      }
    }

    // Gorgias chat trigger
    if (this.dom.triggerGorgiasChat) {
      const chatHandler = () => this.#handleGorgiasChat();
      this.dom.triggerGorgiasChat.addEventListener("click", chatHandler);
      this.eventHandlers.push({ element: this.dom.triggerGorgiasChat, event: "click", handler: chatHandler });
    }

    // Mobile hamburger toggle
    if (this.dom.navToggle) {
      const toggleHandler = () => this.#toggleMobileNav();
      this.dom.navToggle.addEventListener("click", toggleHandler);
      this.eventHandlers.push({ element: this.dom.navToggle, event: "click", handler: toggleHandler });
    }

    // SR-only close button inside mobile nav dialog (#226)
    if (this.dom.mobileNavClose) {
      const mobileCloseHandler = () => this.#closeMobileNav();
      this.dom.mobileNavClose.addEventListener("click", mobileCloseHandler);
      this.eventHandlers.push({ element: this.dom.mobileNavClose, event: "click", handler: mobileCloseHandler });
    }

    // Sub-navigation triggers (click)
    this.dom.navTriggers.forEach(trigger => {
      const clickHandler = () => this.#handleNavTriggerClick(trigger);
      trigger.addEventListener("click", clickHandler);
      this.eventHandlers.push({ element: trigger, event: "click", handler: clickHandler });

      // Desktop hover behavior
      if (window.innerWidth >= 1280) {
        const hoverHandler = () => this.#handleNavTriggerHover(trigger);
        trigger.addEventListener("mouseover", hoverHandler);
        this.eventHandlers.push({ element: trigger, event: "mouseover", handler: hoverHandler });
      }
    });

    // Handle viewport resize to manage inert state
    let resizeTimer;
    const resizeHandler = () => {
      document.body.classList.add("resize-animation-stopper");
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        document.body.classList.remove("resize-animation-stopper");
      }, 400);

      if (this.dom.navDrawer) {
        if (window.innerWidth >= 1280) {
          // Desktop: Always interactive
          this.dom.navDrawer.removeAttribute("inert");
        } else {
          // Mobile: Interactive only if open
          if (this.dom.navDrawer.getAttribute("aria-hidden") === "true") {
            this.dom.navDrawer.setAttribute("inert", "");
          } else {
            this.dom.navDrawer.removeAttribute("inert");
          }
        }
      }
    };
    window.addEventListener("resize", resizeHandler);
    this.eventHandlers.push({ element: window, event: "resize", handler: resizeHandler });

    // Back navigation buttons
    this.dom.navBack.forEach(back => {
      const backHandler = () => this.#handleNavBack(back);
      back.addEventListener("click", backHandler);
      this.eventHandlers.push({ element: back, event: "click", handler: backHandler });
    });

    // Accordion menus
    this.dom.navAccordions.forEach(accordion => {
      const accordionHandler = () => this.#handleAccordion(accordion);
      accordion.addEventListener("click", accordionHandler);
      this.eventHandlers.push({ element: accordion, event: "click", handler: accordionHandler });
    });

    // Desktop drawer triggers
    // Desktop drawer triggers
    this.dom.desktopNavTriggers.forEach(trigger => {
      const desktopHandler = e => {
        if (window.innerWidth >= 1280) {
          this.#handleDesktopDrawer(trigger, e);
        }
      };
      trigger.addEventListener("click", desktopHandler);
      this.eventHandlers.push({ element: trigger, event: "click", handler: desktopHandler });
    });

    // Drawer triggers
    this.dom.drawerTriggers.forEach(drawerTrigger => {
      const drawerHandler = e => this.#handleDrawerTrigger(drawerTrigger, e);
      drawerTrigger.addEventListener("click", drawerHandler);
      this.eventHandlers.push({ element: drawerTrigger, event: "click", handler: drawerHandler });
    });

    // Overlay click to close
    if (this.dom.overlay) {
      const overlayHandler = () => this.#closeDrawers();
      this.dom.overlay.addEventListener("click", overlayHandler);
      this.eventHandlers.push({ element: this.dom.overlay, event: "click", handler: overlayHandler });
    }

    // Prevent clicks inside navigation drawer from closing it
    if (this.dom.navDrawer) {
      const stopPropagationHandler = e => e.stopPropagation();
      this.dom.navDrawer.addEventListener("click", stopPropagationHandler);
      this.eventHandlers.push({ element: this.dom.navDrawer, event: "click", handler: stopPropagationHandler });
    }

    // Close button
    if (this.dom.closeNavBtn) {
      const closeHandler = () => this.#closeDrawers();
      this.dom.closeNavBtn.addEventListener("click", closeHandler);
      this.eventHandlers.push({ element: this.dom.closeNavBtn, event: "click", handler: closeHandler });
    }

    // Header click to close navigation drawer (but not when clicking nav toggle or inside drawer)
    // if (this.dom.header) {
    //   const headerClickHandler = e => {
    //     // Don't close if clicking on nav toggle or inside navigation drawer
    //     if (this.dom.navToggle?.contains(e.target) || this.dom.navDrawer?.contains(e.target)) {
    //       return;
    //     }
    //     // Close drawer if it's open
    //     if (this.dom.navDrawer?.getAttribute("aria-hidden") === "false") {
    //       this.#closeDrawers();
    //     }
    //   };
    //   this.dom.header.addEventListener("click", headerClickHandler);
    //   this.eventHandlers.push({ element: this.dom.header, event: "click", handler: headerClickHandler });
    // }
  }

  /**
   * Handle Gorgias chat trigger
   * @private
   */
  #handleGorgiasChat() {
    if (window.innerWidth < 1024) {
      this.#closeMobileNav();
    } else {
      this.#closeDrawers();
    }

    // Open Gorgias chat
    if (typeof GorgiasChat !== "undefined") {
      GorgiasChat.open();
      GorgiasChat.hidePoweredBy(true);
      const gorgiasClose = document.getElementById("gorgiasClose");
      if (gorgiasClose && window.innerWidth > 992) {
        gorgiasClose.classList.remove("hide");
      }
    }
  }

  /**
   * Initialize carousels in the drawer when it becomes visible
   * Waits for carousel.js to be ready before initializing
   * @private
   */
  #initializeDrawerCarousels() {
    const initialize = () => {
      if (typeof window.initCarousels === "function") {
        // false = don't skip hidden, since drawer is now visible
        window.initCarousels(".swiper__carousel", false);
      }
    };

    // Check if carousel.js is already ready
    if (typeof window.initCarousels === "function") {
      // Carousel.js is loaded, initialize immediately
      initialize();
    } else {
      // Wait for carousel:ready event
      const handleCarouselReady = () => {
        initialize();
        document.removeEventListener("carousel:ready", handleCarouselReady);
      };
      document.addEventListener("carousel:ready", handleCarouselReady);
    }
  }

  /**
   * Get all visible, focusable elements within a container.
   * Accounts for desktop drawer structure where drawer triggers span multiple wrappers.
   * @param {HTMLElement} container
   * @returns {HTMLElement[]}
   * @private
   */
  #getFocusableElements(container) {
    const selector = [
      "a[href]",
      "button:not([disabled])",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      '[tabindex]:not([tabindex="-1"])',
    ].join(", ");

    return [...container.querySelectorAll(selector)].filter(el => {
      if (el.closest("[inert]")) return false;
      if (el.offsetParent === null && getComputedStyle(el).position !== "fixed") return false;

      if (el.classList.contains("sidebarMenu__drawerTrigger")) {
        return el.classList.contains("is--visible");
      }

      const wrapper = el.closest(".sidebarMenu__drawerWrapper");
      if (wrapper && !wrapper.classList.contains("is--active")) return false;

      return true;
    });
  }

  /**
   * Trap keyboard focus within a container so Tab / Shift+Tab wraps around
   * @param {HTMLElement} container
   * @private
   */
  #activateFocusTrap(container) {
    this.#deactivateFocusTrap();

    this._focusTrapContainer = container;
    this._focusTrapHandler = e => {
      if (e.key !== "Tab") return;

      const focusable = this.#getFocusableElements(this._focusTrapContainer);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", this._focusTrapHandler);
  }

  /**
   * Remove the active focus trap
   * @private
   */
  #deactivateFocusTrap() {
    if (this._focusTrapHandler) {
      document.removeEventListener("keydown", this._focusTrapHandler);
      this._focusTrapHandler = null;
      this._focusTrapContainer = null;
    }
  }

  /**
   * Toggle mobile navigation drawer (hamburger button handler)
   * @private
   */
  #toggleMobileNav() {
    const hamburger = this.dom.navToggle?.querySelector(".icon--menu");
    const close = this.dom.navToggle?.querySelector(".icon--menuClose");

    if (!hamburger || !close || !this.dom.navDrawer) return;

    if (hamburger.style.display !== "none") {
      hamburger.style.display = "none";
      close.style.display = "block";

      if (this.dom.navDrawer.getAttribute("aria-hidden") === "true") {
        this.dom.navDrawer.setAttribute("aria-hidden", "false");
        this.dom.navDrawer.removeAttribute("inert");
        document.body.classList.add("drawer-open", "menu--open");

        if (this.dom.overlay) {
          this.dom.overlay.classList.add("is--active");
        }

        this.#initializeDrawerCarousels();

        const firstFocusable = this.dom.navDrawer.querySelector(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );

        if (firstFocusable) {
          firstFocusable.focus();
        }

        this.#activateFocusTrap(this.dom.navDrawer);
      }
    } else {
      this.#closeMobileNav();
    }
  }

  /**
   * Close the mobile navigation drawer and return focus to the toggle
   * @private
   */
  #closeMobileNav() {
    if (!this.dom.navDrawer) return;

    const hamburger = this.dom.navToggle?.querySelector(".icon--menu");
    const close = this.dom.navToggle?.querySelector(".icon--menuClose");

    if (hamburger) hamburger.style.display = "block";
    if (close) close.style.display = "none";

    if (this.dom.navDrawer.getAttribute("aria-hidden") === "false") {
      const focusedElement = this.dom.navDrawer.querySelector(":focus");

      if (focusedElement && focusedElement instanceof HTMLElement) {
        focusedElement.blur();
      }

      this.dom.navDrawer.setAttribute("aria-hidden", "true");
      this.dom.navDrawer.setAttribute("inert", "");
      this.#deactivateFocusTrap();
      document.body.classList.remove("drawer-open", "menu--open");

      if (this.dom.overlay) {
        this.dom.overlay.classList.remove("is--active");
      }
    }

    this.dom.navToggle?.focus();
  }

  /**
   * Handle navigation trigger click
   * @param {HTMLElement} trigger - The trigger element
   * @private
   */
  #handleNavTriggerClick(trigger) {
    if (trigger.dataset.navChild === "false") return;
    const subMenu = trigger.nextElementSibling;
    if (subMenu) {
      subMenu.classList.add("is--active");
      if (this.dom.navDrawer) {
        this.dom.navDrawer.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    }
  }

  /**
   * Handle navigation trigger hover (desktop only)
   * @param {HTMLElement} trigger - The trigger element
   * @private
   */
  #handleNavTriggerHover(trigger) {
    // Set all other triggers as inactive
    this.dom.navTriggers.forEach(t => {
      if (trigger.dataset.navChild === "true") {
        t.classList.add("is--inactive");
      } else {
        t.classList.remove("is--inactive");
      }
    });
    trigger.classList.remove("is--inactive");

    // Close other open submenus
    const openSubMenus = this.querySelectorAll(".sidebarMenu__subMenu.is--active");
    openSubMenus.forEach(openSubMenu => {
      if (openSubMenu !== trigger.nextElementSibling) {
        openSubMenu.classList.remove("is--active");
      }
    });

    if (trigger.dataset.navChild === "false") return;
    const subMenu = trigger.nextElementSibling;
    if (subMenu) {
      subMenu.classList.add("is--active");
    }
  }

  /**
   * Handle back navigation
   * @param {HTMLElement} back - The back button element
   * @private
   */
  #handleNavBack(back) {
    const subMenu = back.closest(".is--active");
    if (subMenu) {
      subMenu.classList.remove("is--active");
    }
  }

  /**
   * Handle accordion menu toggle
   * @param {HTMLElement} accordion - The accordion element
   * @private
   */
  #handleAccordion(accordion) {
    const content = accordion.nextElementSibling;
    if (!content) return;

    if (accordion.classList.contains("isExpanded")) {
      // Collapse
      accordion.classList.remove("isExpanded");
      content.style.height = "0px";
      content.classList.remove("isOpen");
    } else {
      // Collapse any other expanded accordion
      const expanded = this.querySelector(".js-menuAccordion.isExpanded");
      if (expanded && expanded !== accordion) {
        expanded.classList.remove("isExpanded");
        const expandedContent = expanded.nextElementSibling;
        if (expandedContent) {
          expandedContent.style.height = "0px";
          expandedContent.classList.remove("isOpen");
        }
      }

      // Expand current accordion
      accordion.classList.add("isExpanded");
      content.classList.add("isOpen");
      content.style.height = content.scrollHeight + "px";
    }
  }

  /**
   * Handle desktop drawer trigger
   * @param {HTMLElement} trigger - The trigger element
   * @param {Event} e - The click event
   * @private
   */
  #handleDesktopDrawer(trigger, e) {
    const contactMenu = document.querySelector(".sidebarMenu__contact");
    const openDrawers = document.querySelectorAll(".sidebarMenu__drawerWrapper.is--active");

    // Close other open drawers
    openDrawers.forEach(openDrawer => {
      if (openDrawer !== e.target.closest(".sidebarMenu__drawerWrapper")) {
        openDrawer.classList.remove("is--active");
      }
    });

    const drawer = e.target.nextElementSibling;
    if (drawer && !drawer.classList.contains("is--active")) {
      this.#lockBodyScroll();
      drawer.classList.add("is--active");
      document.body.classList.add("menu--open");

      this.dom.drawerTriggers.forEach(drawerTrigger => {
        drawerTrigger.classList.add("is--visible");
        if (drawerTrigger.dataset.trigger === trigger.dataset.trigger) {
          drawerTrigger.classList.add("is--active");
        } else {
          drawerTrigger.classList.remove("is--active");
        }
      });

      // Move contact menu to drawer
      if (contactMenu) {
        const desktopDrawer = drawer.querySelector(".sidebarMenu__subNavWrapper");
        if (desktopDrawer) {
          desktopDrawer.appendChild(contactMenu);
        }
      }

      if (this.dom.overlay) {
        this.dom.overlay.classList.add("is--active");
      }
    }

    if (this.dom.closeNavBtn && !this.dom.closeNavBtn.classList.contains("is--visible")) {
      this.dom.closeNavBtn.classList.add("is--visible");
    }

    this._lastActiveTrigger = trigger;

    if (this.dom.closeNavBtn) {
      this.dom.closeNavBtn.focus();
    }

    if (this.dom.navDrawer) {
      this.#activateFocusTrap(this.dom.navDrawer);
    }
  }

  /**
   * Handle drawer trigger click
   * @param {HTMLElement} drawerTrigger - The drawer trigger element
   * @param {Event} e - The click event
   * @private
   */
  #handleDrawerTrigger(drawerTrigger, e) {
    // Reset inactive states
    this.dom.navTriggers.forEach(t => {
      t.classList.remove("is--inactive");
    });

    const contactMenu = document.querySelector(".sidebarMenu__contact");
    const openDrawers = document.querySelectorAll(".sidebarMenu__drawerWrapper.is--active");

    // Close other open drawers
    openDrawers.forEach(openDrawer => {
      if (openDrawer !== e.target.closest(".sidebarMenu__drawerWrapper")) {
        openDrawer.classList.remove("is--active");
      }
    });

    const drawer = e.target.parentElement;
    if (drawer && !drawer.classList.contains("is--active")) {
      drawer.classList.add("is--active");
      document.body.classList.add("menu--open");

      // Move contact menu to drawer
      if (contactMenu) {
        const desktopDrawer = drawer.querySelector(".sidebarMenu__subNavWrapper");
        if (desktopDrawer) {
          desktopDrawer.appendChild(contactMenu);
        }
      }

      this.dom.drawerTriggers.forEach(trigger => trigger.classList.remove("is--active"));
      drawerTrigger.classList.add("is--active");

      if (this.dom.overlay) {
        this.dom.overlay.classList.add("is--active");
      }
      this.#lockBodyScroll();
    }
  }

  /**
   * Close all drawers and reset states
   * @private
   */
  #closeDrawers() {
    const drawers = document.querySelectorAll(".sidebarMenu__drawerWrapper.is--active");
    drawers.forEach(drawer => {
      drawer.classList.remove("is--active");
    });

    this.dom.drawerTriggers.forEach(trigger => {
      trigger.classList.remove("is--active");
      trigger.classList.remove("is--visible");
    });

    const openSubMenus = this.querySelectorAll(".sidebarMenu__subMenu.is--active");
    openSubMenus.forEach(openSubMenu => {
      openSubMenu.classList.remove("is--active");
    });

    this.dom.navTriggers.forEach(trigger => trigger.classList.remove("is--inactive"));

    if (this.dom.closeNavBtn?.classList.contains("is--visible")) {
      this.dom.closeNavBtn.classList.remove("is--visible");
    }

    // Close mobile navigation drawer if open
    if (this.dom.navDrawer?.getAttribute("aria-hidden") === "false") {
      // Blur any focused elements inside the drawer before hiding to prevent accessibility violation
      const focusedElement = this.dom.navDrawer.querySelector(":focus");
      if (focusedElement && focusedElement instanceof HTMLElement) {
        focusedElement.blur();
      }
      const hamburger = this.dom.navToggle?.querySelector(".icon--menu");
      const close = this.dom.navToggle?.querySelector(".icon--menuClose");
      if (hamburger) hamburger.style.display = "block";
      if (close) close.style.display = "none";
      this.dom.navDrawer.setAttribute("aria-hidden", "true");
      this.dom.navDrawer.setAttribute("inert", "");
      document.body.classList.remove("drawer-open", "menu--open");
    }

    if (this.dom.overlay) {
      this.dom.overlay.classList.remove("is--active");
    }

    this.#unlockBodyScroll();
    this.#deactivateFocusTrap();

    if (this._lastActiveTrigger && this._lastActiveTrigger.isConnected) {
      this._lastActiveTrigger.focus();
      this._lastActiveTrigger = null;
    } else {
      this._lastActiveTrigger = null;
      this.dom.navToggle?.focus();
    }
  }

  /**
   * Clean up event listeners and Swiper instances
   * @private
   */
  #cleanup() {
    // Remove all event listeners
    this.eventHandlers.forEach(({ element, event, handler }) => {
      if (element) {
        element.removeEventListener(event, handler);
      }
    });
    this.eventHandlers = [];

    // NOTE: Swiper destruction is purposely removed here.
    // The carousel.js library handles lifecycle or the instance should persist
    // to avoid visual flashes if this component is re-connected.
    this.swiperInstances = [];

    // Close drawers and reset state
    this.#closeDrawers();
  }
}

if (!window.customElements.get("navigation-component")) {
  window.customElements.define("navigation-component", NavigationComponent);
}
